import { createSlice } from "@reduxjs/toolkit"
import { jwtDecode } from "jwt-decode"

interface AccessTokenPayload {
  email: string
  organization_id: string
  organization_name: string
  exp: number
  first_name: string
  last_name: string
  role_name: string
  role_id: string
  resources: number[]
  permissions: {
    synapse_id: number[]
    konan: number[]
  }
  role_is_owner: boolean
}

interface RefreshTokenPayload {
  exp: number
}

/**
 * this slice used to store and update workflow listing
 */
export const authSlice = createSlice({
  name: "auth",
  initialState: {
    authToken: undefined as string | undefined,
    refreshToken: undefined as string | undefined,
    email: undefined as string | undefined,
    organizationId: undefined as string | undefined,
    organizationName: undefined as string | undefined,
    accessExpiryDate: undefined as number | undefined,
    firstName: undefined as string | undefined,
    lastName: undefined as string | undefined,
    roleName: undefined as string | undefined,
    roleId: undefined as string | undefined,
    resources: [] as number[],
    permissions: {} as {
      synapse_id: number[]
      konan: number[]
    },
    roleIsOwner: undefined as boolean | undefined,
    refreshExpiryDate: undefined as number | undefined,
    username: undefined as string | undefined,
    isUserAuthenticated: false,
    shouldAuthenticate: true,
  },
  reducers: {
    setAccessToken(state, action) {
      state.authToken = action.payload

      if (action.payload) {
        try {
          const access_payload = jwtDecode(action.payload) as AccessTokenPayload
          state.email = access_payload.email
          state.organizationId = access_payload.organization_id
          state.organizationName = access_payload.organization_name
          state.accessExpiryDate = access_payload.exp
          state.firstName = access_payload.first_name
          state.lastName = access_payload.last_name
          state.roleName = access_payload.role_name
          state.roleId = access_payload.role_id
          state.resources = access_payload.resources
          state.permissions = access_payload.permissions
          state.roleIsOwner = access_payload.role_is_owner
          state.isUserAuthenticated = true
          state.shouldAuthenticate = false
        } catch (e) {
          // If JWT decode fails, only set the token
          console.warn("Failed to decode access token:", e)
        }
      }
    },
    setRefreshToken(state, action) {
      state.refreshToken = action.payload

      if (action.payload) {
        try {
          const refresh_payload = jwtDecode(action.payload) as RefreshTokenPayload
          state.refreshExpiryDate = refresh_payload.exp
        } catch (e) {
          // If JWT decode fails, only set the token
          console.warn("Failed to decode refresh token:", e)
        }
      }
    },

    // Login method that handles both tokens
    setAuthTokens(state, action) {
      const { access, refresh } = action.payload

      // Handle access token
      if (access) {
        state.authToken = access
        try {
          const access_payload = jwtDecode(access) as AccessTokenPayload
          state.email = access_payload.email
          state.organizationId = access_payload.organization_id
          state.organizationName = access_payload.organization_name
          state.accessExpiryDate = access_payload.exp
          state.firstName = access_payload.first_name
          state.lastName = access_payload.last_name
          state.roleName = access_payload.role_name
          state.roleId = access_payload.role_id
          state.resources = access_payload.resources
          state.permissions = access_payload.permissions
          state.roleIsOwner = access_payload.role_is_owner
          state.isUserAuthenticated = true
          state.shouldAuthenticate = false
        } catch (e) {
          console.warn("Failed to decode access token:", e)
        }
      }

      // Handle refresh token
      if (refresh) {
        state.refreshToken = refresh
        try {
          const refresh_payload = jwtDecode(refresh) as RefreshTokenPayload
          state.refreshExpiryDate = refresh_payload.exp
        } catch (e) {
          console.warn("Failed to decode refresh token:", e)
        }
      }
    },

    clearAuthData(state) {
      state.authToken = undefined
      state.refreshToken = undefined
      state.email = undefined
      state.organizationId = undefined
      state.organizationName = undefined
      state.accessExpiryDate = undefined
      state.firstName = undefined
      state.lastName = undefined
      state.roleName = undefined
      state.roleId = undefined
      state.resources = []
      state.permissions = { synapse_id: [], konan: [] }
      state.roleIsOwner = undefined
      state.refreshExpiryDate = undefined
      state.username = undefined
      state.isUserAuthenticated = false
      state.shouldAuthenticate = false
    },

    setIsUserAuthenticated(state, action) {
      state.isUserAuthenticated = action.payload
    },

    setShouldAuthenticate(state, action) {
      state.shouldAuthenticate = action.payload
    },

    // User data setters
    setUsername(state, action) {
      state.username = action.payload
    },

    setFirstName(state, action) {
      state.firstName = action.payload
    },

    setLastName(state, action) {
      state.lastName = action.payload
    },

    setOrganizationUID(state, action) {
      state.organizationId = action.payload
    },

    setOrganizationName(state, action) {
      state.organizationName = action.payload
    },
  },
})

export const {
  setRefreshToken,
  clearAuthData,
  setAuthTokens,
  setUsername,
  setFirstName,
  setLastName,
  setOrganizationUID,
  setOrganizationName,
  setAccessToken,
  setIsUserAuthenticated,
  setShouldAuthenticate,
} = authSlice.actions

export const authReducer = authSlice.reducer
